import { useApolloClient } from "@apollo/client";
import { useCallback } from "react";
import { PageInfo, Query, Subject } from "@models/ISchema";
import SUBJECTS_QUERY from "./graphql/subjects.query.graphql";

interface SearchSubjectsParams {
  scenarioId: string;
  originId: string;
  searchTerm?: string;
  page?: number;
  size?: number;
}

export interface SearchSubjectsResult {
  subjects: Subject[];
  pageInfo: PageInfo | null;
}

const DEFAULT_PAGE_SIZE = 20;

/**
 * Typeahead search over the scenario's subjects. Uses `client.query` instead of
 * `useLazyQuery` because the consumer is `SelectPagination`, whose `loadOptions`
 * has to await the result for each keystroke and page.
 */
export const useSearchSubjects = () => {
  const client = useApolloClient();

  const searchSubjects = useCallback(
    async ({
      scenarioId,
      originId,
      searchTerm = "",
      page = 1,
      size = DEFAULT_PAGE_SIZE,
    }: SearchSubjectsParams): Promise<SearchSubjectsResult> => {
      try {
        const { data } = await client.query<Query>({
          query: SUBJECTS_QUERY,
          variables: {
            scenarioId,
            originId,
            filter: {
              pagination: { page, size, searchTerm },
            },
          },
        });

        return {
          subjects: (data?.data?.subjects?.items ?? []) as Subject[],
          pageInfo: (data?.data?.subjects?.pageInfo ?? null) as PageInfo | null,
        };
      } catch (error) {
        console.error(error);

        return { subjects: [], pageInfo: null };
      }
    },
    [client],
  );

  return { searchSubjects };
};
